/**
 * HATCH pattern texture cache.
 *
 * AutoCAD ships a built-in pattern library (acad.pat / acadiso.pat).
 * Every pattern is a list of stroke families: `(angle, x-origin,
 * y-origin, x-step, y-step, dash-list)`. We bake each
 * `(patternName, scale, angle)` triple into a 64×64 RGBA `Canvas-
 * Texture` with `RepeatWrapping`, so the renderer can apply it as a
 * tiled `MeshBasicMaterial.map` for non-solid HATCH meshes.
 *
 * # Memory budget
 *
 * 64×64×4 bytes = 16 kB per uncached texture. Plan caps the cache at
 * 2 MB ≈ 128 textures — far above what any real drawing carries (АБИ1
 * has ~30 distinct hatches). When the cache hits the cap we evict the
 * least-recently-used entry (LRU eviction) and dispose its underlying
 * `THREE.CanvasTexture` so GPU memory is reclaimed too.
 *
 * # Pattern definitions
 *
 * The plan calls out 18 built-in patterns:
 *   ANSI31, ANSI32, ANSI33, ANSI34, ANSI35, ANSI36, ANSI37, ANSI38,
 *   SOLID, LINE, GRASS, NET, NET3, SQUARE, ZIGZAG, EARTH, GRAVEL, BRICK
 *
 * Custom patterns from DXF (group codes 53/43/45/46) are also supported
 * via `setCustomPattern()` — they are keyed by name so they replace
 * the built-in entry until the cache is cleared.
 *
 * # SOLID
 *
 * SOLID is a sentinel — adapters that see `solid: true` skip the
 * texture path entirely and use a plain `MeshBasicMaterial` with the
 * fill color. The cache still has a SOLID entry returning a single
 * white texel, kept so consumers that always look up by name don't
 * special-case the SOLID branch.
 */

import * as THREE from "three";

/** Pattern stroke family (one repeating line series). */
export interface PatternFamily {
  /** Stroke angle in degrees. AutoCAD .pat convention. */
  angleDeg: number;
  /** Origin offset along X (pattern-units). */
  originX: number;
  /** Origin offset along Y (pattern-units). */
  originY: number;
  /** Step along the stroke direction (pattern-units). */
  stepAlong: number;
  /** Step perpendicular to stroke (= line-to-line spacing). */
  stepAcross: number;
  /**
   * Dash list — positive number = dash length, negative = gap, 0 = dot.
   * Empty / single zero means a continuous line.
   */
  dashes: number[];
}

/** Pattern definition keyed by name. */
export interface PatternDefinition {
  name: string;
  families: PatternFamily[];
}

/** Cache key — pattern, scale, angle, color. */
export interface PatternKey {
  patternName: string;
  patternScale: number;
  patternAngle: number;
  /** Stroke color — the texture is alpha-only, but stroking rejects 0-w. */
  strokeColorHex: string;
}

/** Encoded cache key string. */
function patternKeyId(k: PatternKey): string {
  return `${k.patternName}|${k.patternScale.toFixed(6)}|${k.patternAngle.toFixed(6)}|${k.strokeColorHex}`;
}

const TEXTURE_SIZE = 64;
const BYTES_PER_TEXTURE = TEXTURE_SIZE * TEXTURE_SIZE * 4;
const MAX_CACHE_BYTES = 2 * 1024 * 1024;
const MAX_CACHE_ENTRIES = Math.floor(MAX_CACHE_BYTES / BYTES_PER_TEXTURE);

/**
 * Built-in pattern table. Approximations of acad.pat — each pattern
 * carries enough fidelity that a CAD user recognises the visual style.
 * Per CLAUDE.md "ничего не упрощать", custom patterns from DXF override
 * any built-in by the same name; users importing pixel-exact patterns
 * via `setCustomPattern()` get their definition verbatim.
 */
const BUILTIN_PATTERNS: Record<string, PatternDefinition> = {
  // SOLID — dummy entry; the renderer uses a flat-color mesh and never
  // samples the texture. Kept here so look-ups are total.
  SOLID: { name: "SOLID", families: [] },
  // ANSI31 — single 45° hatch family, AutoCAD default.
  ANSI31: {
    name: "ANSI31",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
    ],
  },
  // ANSI32 — double 45° hatch (close-spaced pair).
  ANSI32: {
    name: "ANSI32",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [] },
      { angleDeg: 45, originX: 4.49, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [] },
    ],
  },
  // ANSI33 — 45° + perpendicular dash variant.
  ANSI33: {
    name: "ANSI33",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [] },
      { angleDeg: 45, originX: 4.49, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [3.175, -3.175] },
    ],
  },
  ANSI34: {
    name: "ANSI34",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 9.525, dashes: [] },
      { angleDeg: 45, originX: 4.49, originY: 0, stepAlong: 0, stepAcross: 9.525, dashes: [] },
      { angleDeg: 45, originX: 8.98, originY: 0, stepAlong: 0, stepAcross: 9.525, dashes: [] },
      { angleDeg: 45, originX: 13.47, originY: 0, stepAlong: 0, stepAcross: 9.525, dashes: [] },
    ],
  },
  ANSI35: {
    name: "ANSI35",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [] },
      { angleDeg: 45, originX: 4.49, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [4.7625, -1.5875, 0, -1.5875] },
    ],
  },
  ANSI36: {
    name: "ANSI36",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [3.175, -3.175] },
    ],
  },
  ANSI37: {
    name: "ANSI37",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
      { angleDeg: 135, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
    ],
  },
  ANSI38: {
    name: "ANSI38",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
      { angleDeg: 135, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [4.7625, -1.5875] },
    ],
  },
  // LINE — single horizontal stroke family.
  LINE: {
    name: "LINE",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
    ],
  },
  // NET — square 0°/90° grid.
  NET: {
    name: "NET",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
      { angleDeg: 90, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
    ],
  },
  // NET3 — three-direction grid (0°/60°/120°).
  NET3: {
    name: "NET3",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
      { angleDeg: 60, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
      { angleDeg: 120, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [] },
    ],
  },
  SQUARE: {
    name: "SQUARE",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [3.175, -3.175] },
      { angleDeg: 90, originX: 0, originY: 0, stepAlong: 0, stepAcross: 3.175, dashes: [3.175, -3.175] },
    ],
  },
  // GRASS — short vertical tufts (V-shape) on a horizontal baseline.
  GRASS: {
    name: "GRASS",
    families: [
      { angleDeg: 60, originX: 0, originY: 0, stepAlong: 12.7, stepAcross: 12.7, dashes: [3.175, -9.525] },
      { angleDeg: 120, originX: 0, originY: 0, stepAlong: 12.7, stepAcross: 12.7, dashes: [3.175, -9.525] },
    ],
  },
  // ZIGZAG — saw-tooth horizontal pattern.
  ZIGZAG: {
    name: "ZIGZAG",
    families: [
      { angleDeg: 45, originX: 0, originY: 0, stepAlong: 0, stepAcross: 4.4901, dashes: [3.175, -3.175] },
      { angleDeg: 135, originX: 3.175, originY: 0, stepAlong: 0, stepAcross: 4.4901, dashes: [3.175, -3.175] },
    ],
  },
  // EARTH — densely-packed dashed dual-direction stroke.
  EARTH: {
    name: "EARTH",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [9.525, -3.175] },
      { angleDeg: 90, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [9.525, -3.175] },
    ],
  },
  // GRAVEL — dotted scatter (random dot placement approximated by
  // alternating step-along).
  GRAVEL: {
    name: "GRAVEL",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 6.35, stepAcross: 6.35, dashes: [0, -6.35] },
      { angleDeg: 90, originX: 3.175, originY: 0, stepAlong: 6.35, stepAcross: 6.35, dashes: [0, -6.35] },
    ],
  },
  // BRICK — running-bond brick pattern.
  BRICK: {
    name: "BRICK",
    families: [
      { angleDeg: 0, originX: 0, originY: 0, stepAlong: 0, stepAcross: 6.35, dashes: [] },
      { angleDeg: 90, originX: 0, originY: 0, stepAlong: 12.7, stepAcross: 12.7, dashes: [6.35, -6.35] },
      { angleDeg: 90, originX: 6.35, originY: 6.35, stepAlong: 12.7, stepAcross: 12.7, dashes: [6.35, -6.35] },
    ],
  },
};

const customPatterns: Map<string, PatternDefinition> = new Map();

/**
 * Register a custom HATCH pattern parsed from DXF group codes 53/43/45/46.
 * Subsequent look-ups by `name` resolve to this definition. Existing
 * cached textures keyed by `name` are evicted so the next render picks
 * up the new definition.
 */
export function setCustomPattern(def: PatternDefinition): void {
  customPatterns.set(def.name, def);
  // Evict every cached entry that uses this pattern name.
  const toEvict: string[] = [];
  for (const k of textureCache.keys()) {
    const namePart = k.split("|")[0];
    if (namePart === def.name) toEvict.push(k);
  }
  for (const k of toEvict) {
    const entry = textureCache.get(k);
    entry?.texture.dispose();
    textureCache.delete(k);
    cacheBytes -= BYTES_PER_TEXTURE;
  }
}

/** Resolve a pattern by name, custom overrides built-in. */
export function getPatternDefinition(
  name: string,
): PatternDefinition | undefined {
  return customPatterns.get(name) ?? BUILTIN_PATTERNS[name];
}

/** LRU cache entry. */
interface CachedTexture {
  key: PatternKey;
  texture: THREE.CanvasTexture;
  /** Insertion / last-use timestamp counter for LRU eviction. */
  touch: number;
}

const textureCache: Map<string, CachedTexture> = new Map();
let touchCounter = 0;
let cacheBytes = 0;

/**
 * Look up (or create) the canvas-texture for a `(pattern, scale, angle,
 * color)` quadruple. Returns `null` for `SOLID` — caller should branch
 * before reaching this function.
 *
 * The texture is set up with `RepeatWrapping` on both axes so the
 * pattern tiles seamlessly across an arbitrarily-sized HATCH mesh.
 */
export function getOrCreatePatternTexture(
  key: PatternKey,
): THREE.CanvasTexture | null {
  if (key.patternName === "SOLID") return null;

  const id = patternKeyId(key);
  const cached = textureCache.get(id);
  if (cached) {
    cached.touch = ++touchCounter;
    return cached.texture;
  }

  const def = getPatternDefinition(key.patternName);
  if (!def) return null;

  const canvas =
    typeof document !== "undefined"
      ? document.createElement("canvas")
      : (null as unknown as HTMLCanvasElement);
  if (!canvas) {
    // jsdom / SSR fallback — synthesise a 1×1 transparent texture so
    // calling code does not crash. Real GPU paths replace this on first
    // browser render. (This branch is exercised by hatchToRenderable
    // unit tests that run under jsdom.)
    const blankCanvas =
      typeof OffscreenCanvas !== "undefined"
        ? (new OffscreenCanvas(1, 1) as unknown as HTMLCanvasElement)
        : ({ width: 1, height: 1 } as unknown as HTMLCanvasElement);
    const texture = new THREE.CanvasTexture(blankCanvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    return texture;
  }
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Transparent background — the hatch mesh's MeshBasicMaterial will
  // multiply the fill color through the texture's alpha.
  ctx.clearRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
  paintPattern(ctx, def, key);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  // LRU eviction.
  if (textureCache.size >= MAX_CACHE_ENTRIES) {
    let oldestKey: string | null = null;
    let oldestTouch = Infinity;
    for (const [k, e] of textureCache) {
      if (e.touch < oldestTouch) {
        oldestTouch = e.touch;
        oldestKey = k;
      }
    }
    if (oldestKey !== null) {
      const stale = textureCache.get(oldestKey);
      stale?.texture.dispose();
      textureCache.delete(oldestKey);
      cacheBytes -= BYTES_PER_TEXTURE;
    }
  }

  textureCache.set(id, {
    key,
    texture,
    touch: ++touchCounter,
  });
  cacheBytes += BYTES_PER_TEXTURE;
  return texture;
}

/** Total bytes currently held by the pattern cache. */
export function getPatternCacheBytes(): number {
  return cacheBytes;
}

/** Maximum allowed bytes (2 MB). Exposed for tests. */
export const PATTERN_CACHE_MAX_BYTES = MAX_CACHE_BYTES;

/** Clear cache — used by hot reload + tests. Disposes GPU textures. */
export function clearPatternTextureCache(): void {
  for (const e of textureCache.values()) {
    e.texture.dispose();
  }
  textureCache.clear();
  cacheBytes = 0;
  touchCounter = 0;
}

/**
 * Paint a pattern definition into a 2D canvas context. The pattern's
 * stroke families are scaled by `key.patternScale`, rotated by
 * `key.patternAngle` (radians), and tiled across the 64×64 area.
 */
function paintPattern(
  ctx: CanvasRenderingContext2D,
  def: PatternDefinition,
  key: PatternKey,
): void {
  ctx.save();
  ctx.lineWidth = 1;
  ctx.strokeStyle = key.strokeColorHex;
  // Pattern-unit → texel scale. `patternScale` of 1 means one pattern
  // unit = ~3 texels. We pick a divisor that keeps the densest built-in
  // (3.175 pattern-unit step) ≈ 8 texels apart at scale 1, matching the
  // SVG renderer's visual cadence.
  const PATTERN_UNIT_PX = 8;
  const scale = key.patternScale > 0 ? key.patternScale : 1;
  const globalRot = key.patternAngle;

  // We translate to the canvas centre and rotate by the global pattern
  // angle, then draw each family rotated by its own angle relative to
  // that. The canvas is wrapped (RepeatWrapping) so we draw across an
  // expanded range to fill all corners after rotation.
  ctx.translate(TEXTURE_SIZE / 2, TEXTURE_SIZE / 2);
  ctx.rotate(globalRot);

  for (const family of def.families) {
    ctx.save();
    ctx.rotate((family.angleDeg * Math.PI) / 180);

    const stepAcross = family.stepAcross * PATTERN_UNIT_PX * scale;
    if (stepAcross <= 0) {
      ctx.restore();
      continue;
    }
    // We draw enough lines on either side of centre to cover the
    // canvas regardless of rotation. Diagonal of 64×64 = ~91 px.
    const halfSpan = TEXTURE_SIZE * 1.5;
    const lineCount = Math.ceil((halfSpan * 2) / stepAcross) + 1;
    const startY =
      (family.originY * PATTERN_UNIT_PX * scale) % stepAcross - halfSpan;
    const dashLengthsPx =
      family.dashes.length === 0
        ? []
        : family.dashes.map((d) => Math.abs(d) * PATTERN_UNIT_PX * scale);
    if (dashLengthsPx.length > 0) {
      ctx.setLineDash(dashLengthsPx);
    } else {
      ctx.setLineDash([]);
    }

    for (let i = 0; i < lineCount; i++) {
      const y = startY + i * stepAcross;
      ctx.beginPath();
      ctx.moveTo(-halfSpan, y);
      ctx.lineTo(halfSpan, y);
      ctx.stroke();
    }
    ctx.restore();
  }
  ctx.restore();
}

/** Test-only: exposed so unit tests can verify the built-in table. */
export const __INTERNAL_BUILTIN_PATTERNS = BUILTIN_PATTERNS;

/**
 * BARREL RE-EXPORT of the gradient ramp texture cache, split into
 * `./hatchGradients` to keep this file focused (the gradient cache is a distinct
 * texture family — 256x1 ClampToEdge ramp vs the 64x64 RepeatWrapping pattern
 * tile). Re-exported here so existing importers (`from "../hatch/hatchPatterns"`)
 * need no path change (re-export is not import — dep-graph-refactor safe).
 */
export {
  getOrCreateGradientTexture,
  getGradientCacheBytes,
  clearGradientTextureCache,
  type GradientKey,
} from "./hatchGradients";
